/**
 * **The sheet** (E01, Stream E): the frame every Paper-family print reads — the mark top-left,
 * the title ruled under it, the QR block top-right with its code under it, and the footer on
 * every sheet: its name and what it is for at the left, "CIDER · printed …" at the right. The
 * size is one of E01's seven; everything inside reads that size's scale from `sheet.css`.
 */
import type { ReactNode } from 'react';
import { printedLine, type SheetSize } from './tokens.js';

export interface SheetProps {
  size: SheetSize;
  title: ReactNode;
  sub?: ReactNode | undefined;
  /** The QR CIDER reads back — its drawing and the code printed under it. One a sheet. */
  qr?: { drawing: ReactNode; code: string } | undefined;
  /** The footer's left: the sheet's name and what it is for. */
  name: string;
  /** When it was printed, already in words (`formatDateTime`). */
  printed: string;
  children?: ReactNode;
}

export function Sheet({ size, title, sub, qr, name, printed, children }: SheetProps): ReactNode {
  return (
    <section className="pr-sheet" data-size={size} aria-label={name}>
      <div className="pr-mast">
        <img className="pr-mark" src="/paper-logo.png" alt="Apple Fest" />
        <span className="pr-titles">
          <h1 className="pr-title">{title}</h1>
          {sub ? <span className="pr-sub">{sub}</span> : null}
        </span>
        {qr ? (
          <span className="pr-qr">
            {qr.drawing}
            <span className="pr-qr-code">{qr.code}</span>
          </span>
        ) : null}
      </div>
      {children}
      <div className="pr-foot">
        <span>{name}</span>
        <span>{printedLine(printed)}</span>
      </div>
    </section>
  );
}

/** A chip on paper: the word in a box. `gap` is the one red — a gap or an over. */
export function PaperChip({ children, gap }: { children: ReactNode; gap?: boolean }): ReactNode {
  return (
    <span className="pr-chip" data-gap={gap ? 'yes' : undefined}>
      {children}
    </span>
  );
}
